import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import { Upload, Image, Type, Hash, Save } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppBranding = () => {
  const { user } = React.useContext(AuthContext);
  const [branding, setBranding] = useState({
    symbol_url: '',
    wordmark_url: '',
    tagline: '',
    primary_color: '#3A7BFF',
    secondary_color: '#39E6C8'
  });
  const [loading, setLoading] = useState(false);
  const [symbolFile, setSymbolFile] = useState(null);
  const [wordmarkFile, setWordmarkFile] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchBranding();
    }
  }, [user]);

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/admin/app-branding`, { withCredentials: true });
      if (response.data) {
        setBranding(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    }
  };

  const handleSymbolUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSymbolFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranding(prev => ({ ...prev, symbol_url: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWordmarkUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setWordmarkFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranding(prev => ({ ...prev, wordmark_url: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (symbolFile) formData.append('symbol', symbolFile);
      if (wordmarkFile) formData.append('wordmark', wordmarkFile);
      formData.append('tagline', branding.tagline);
      formData.append('primary_color', branding.primary_color);
      formData.append('secondary_color', branding.secondary_color);

      await axios.post(`${API}/admin/app-branding`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('App branding updated successfully!');
      fetchBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update branding');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8">
        <div className="text-center text-gray-400">
          Admin access required
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">App Branding</h1>
        <p className="text-gray-400">Customize the app's visual identity</p>
      </div>

      <div className="bg-[#1a1d23] rounded-lg border border-gray-800 p-6 space-y-6">
        {/* Symbol Upload */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            <Image className="inline w-4 h-4 mr-2" />
            App Symbol (Logo Icon)
          </label>
          <div className="flex items-center gap-4">
            {branding.symbol_url && (
              <div className="w-24 h-24 bg-gray-900 rounded-lg flex items-center justify-center border border-gray-700">
                <img 
                  src={branding.symbol_url} 
                  alt="Symbol" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            <label className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-gray-700 hover:border-cyan-500 rounded-lg p-4 text-center transition-colors">
                <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-400">
                  Click to upload symbol (PNG, SVG recommended)
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleSymbolUpload}
                  className="hidden"
                />
              </div>
            </label>
          </div>
        </div>

        {/* Wordmark Upload */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            <Type className="inline w-4 h-4 mr-2" />
            App Wordmark (Full Logo with Text)
          </label>
          <div className="flex items-center gap-4">
            {branding.wordmark_url && (
              <div className="w-48 h-24 bg-gray-900 rounded-lg flex items-center justify-center border border-gray-700 px-4">
                <img 
                  src={branding.wordmark_url} 
                  alt="Wordmark" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            <label className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-gray-700 hover:border-cyan-500 rounded-lg p-4 text-center transition-colors">
                <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-400">
                  Click to upload wordmark (PNG, SVG recommended)
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleWordmarkUpload}
                  className="hidden"
                />
              </div>
            </label>
          </div>
        </div>

        {/* Tagline */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            <Hash className="inline w-4 h-4 mr-2" />
            App Tagline
          </label>
          <input
            type="text"
            value={branding.tagline}
            onChange={(e) => setBranding(prev => ({ ...prev, tagline: e.target.value }))}
            placeholder="Your Brand's Tagline"
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
          />
        </div>

        {/* Primary Color */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Primary Brand Color
          </label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={branding.primary_color}
              onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
              className="w-16 h-10 rounded cursor-pointer"
            />
            <input
              type="text"
              value={branding.primary_color}
              onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
              className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>

        {/* Secondary Color */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Secondary Brand Color
          </label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={branding.secondary_color}
              onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
              className="w-16 h-10 rounded cursor-pointer"
            />
            <input
              type="text"
              value={branding.secondary_color}
              onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
              className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#3A7BFF] to-[#39E6C8] hover:opacity-90 text-white font-medium py-3 px-6 rounded-lg transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Branding'}
          </button>
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-[#1a1d23] rounded-lg border border-gray-800 p-6">
        <h2 className="text-xl font-bold text-white mb-4">Preview</h2>
        <div className="space-y-4">
          {/* Symbol Preview */}
          {branding.symbol_url && (
            <div className="flex items-center gap-4 p-4 bg-gray-900 rounded-lg">
              <img src={branding.symbol_url} alt="Symbol" className="w-12 h-12 object-contain" />
              <span className="text-gray-400 text-sm">App Symbol</span>
            </div>
          )}

          {/* Wordmark Preview */}
          {branding.wordmark_url && (
            <div className="flex items-center gap-4 p-4 bg-gray-900 rounded-lg">
              <img src={branding.wordmark_url} alt="Wordmark" className="h-10 object-contain" />
              <span className="text-gray-400 text-sm">App Wordmark</span>
            </div>
          )}

          {/* Tagline Preview */}
          {branding.tagline && (
            <div className="p-4 bg-gray-900 rounded-lg">
              <p className="text-gray-300 italic">"{branding.tagline}"</p>
            </div>
          )}

          {/* Color Preview */}
          <div className="flex gap-4">
            <div className="flex-1 p-4 rounded-lg" style={{ backgroundColor: branding.primary_color }}>
              <p className="text-white font-medium">Primary</p>
            </div>
            <div className="flex-1 p-4 rounded-lg" style={{ backgroundColor: branding.secondary_color }}>
              <p className="text-white font-medium">Secondary</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppBranding;
